import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Button, TextField } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getPresentationCover } from './api';
import { ELEVATION, PRES, TYPE, interactiveResetSx } from './chrome';
import { coverCacheKey } from './coverCache';
import { childDeckCategories, rootDeckCategories } from './deckCategoryTree';
import { formatUpdatedAt } from './formatUpdatedAt';
import LanguageFlags from './LanguageFlags';
import { familyMembersOf, firstOpenLanguageMarket } from './languageVersionHelpers';
import { COPY, languageLabel, marketLabel, pageFormatLabel, slideCountText, statusLabel } from './presentationCopy';
import { PresentationDeckCategory, PresentationFamilyMember, PresentationRecord, PresentationSlide } from './types';
import { CardShell, Pill, SlideThumbnail } from './ui';
import { pillToneForStatus } from './ui/pillTone';
import { focusVisibleSx } from './ui/focus';

const coverCache = new Map<string, PresentationSlide>();

interface CoverCardProps {
  item: PresentationRecord;
  displayName?: string;
  members?: PresentationFamilyMember[];
  openId?: string;
  originReviewPending?: boolean;
  libraryUpdatePending?: boolean;
  canEdit: boolean;
  canCreate?: boolean;
  categories: PresentationDeckCategory[];
  onOpen: (id: string) => void;
  onDelete: () => Promise<void>;
  onRename: (name: string) => Promise<void>;
  onDuplicate: () => Promise<void>;
  onCreateLanguageVersion?: () => void;
  onMoveToCategory: (deckCategoryId: string | null) => Promise<void>;
}

const CoverCard: React.FC<CoverCardProps> = ({
  item,
  displayName,
  members,
  openId,
  originReviewPending,
  libraryUpdatePending,
  canEdit,
  canCreate,
  categories,
  onOpen,
  onDelete,
  onRename,
  onDuplicate,
  onCreateLanguageVersion,
  onMoveToCategory,
}) => {
  const title = displayName || item.name;
  const languageMembers = members?.length ? members : familyMembersOf(item);
  const cardOpenId = openId || item._id;
  const showReview = originReviewPending ?? item.originReviewPending;
  const showLibrary = libraryUpdatePending ?? item.libraryUpdatePending;
  const inLanguageGroup = languageMembers.length > 1;
  const key = coverCacheKey(item);
  const [cover, setCover] = useState<PresentationSlide | null>(() => (key ? coverCache.get(key) || null : null));
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(title);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!item.coverSlideId || !key) {
      setCover(null);
      return;
    }
    const cached = coverCache.get(key);
    if (cached) {
      setCover(cached);
      return;
    }
    const node = rootRef.current;
    if (!node) return;
    const controller = new AbortController();
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      void getPresentationCover(item._id, item.coverSlideId as string, controller.signal)
        .then((slide) => {
          coverCache.set(key, slide);
          setCover(slide);
        })
        .catch(() => setCover(null));
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => {
      observer.disconnect();
      controller.abort();
    };
  }, [item._id, item.coverSlideId, item.updatedAt, key]);

  const canAddLanguageVersion = Boolean(canCreate && onCreateLanguageVersion && firstOpenLanguageMarket(languageMembers));

  const moveTargets = rootDeckCategories(categories).flatMap((root) => [
    { id: root._id, label: root.name },
    ...childDeckCategories(categories, root._id).map((child) => ({ id: child._id, label: `${root.name} / ${child.name}` })),
  ]);

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'relative',
        '&:hover .cover-menu, &:focus-within .cover-menu': { opacity: 1 },
      }}
    >
      <CardShell
        sx={{
          cursor: 'pointer',
          '&:hover': { borderColor: PRES.borderStrong, boxShadow: ELEVATION.cardHover },
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onOpen(cardOpenId)}
          aria-label={`${COPY.open} ${title}`}
          sx={{
            ...interactiveResetSx,
            display: 'block',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            ...focusVisibleSx,
          }}
        >
          <SlideThumbnail slide={cover} pageFormat={item.pageFormat} />
          <Box sx={{ p: 1.5, pb: inLanguageGroup ? 0.75 : 1.5 }}>
            <Box sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary, mb: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
              <Pill tone={pillToneForStatus(item.status)}>{statusLabel(item.status)}</Pill>
              {showReview && (
                <Pill tone="danger" title={COPY.originReviewCardHint} icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}>
                  {COPY.originReviewPill}
                </Pill>
              )}
              {showLibrary && (
                <Pill tone="warn" title={COPY.libraryUpdateCardHint}>{COPY.slideLibrary}</Pill>
              )}
              <Box sx={{ fontFamily: TYPE.ui, ...TYPE.meta, color: PRES.textTertiary }}>
                {slideCountText(item.slideCount || 0)}
              </Box>
            </Box>
            <Box sx={{ fontFamily: TYPE.ui, ...TYPE.meta, color: PRES.textSecondary }}>
              {formatUpdatedAt(item.updatedAt)}
            </Box>
            {!inLanguageGroup && (
              <Box sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mt: 0.35 }}>
                {languageLabel(item.language)} · {marketLabel(item.market)} · {pageFormatLabel(item.pageFormat)}
              </Box>
            )}
          </Box>
        </Box>
        {inLanguageGroup && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', px: 1.5, pb: 1.5 }}
            onClick={() => onOpen(cardOpenId)}
          >
            <LanguageFlags members={languageMembers} onOpen={onOpen} />
            <Box sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary }}>
              {pageFormatLabel(item.pageFormat)}
            </Box>
          </Box>
        )}
      </CardShell>
      {(canEdit || canCreate) && (
        <IconButton
          size="small"
          aria-label={COPY.moreActions}
          onClick={(event) => {
            event.stopPropagation();
            setMenuEl(event.currentTarget);
          }}
          className="cover-menu"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: PRES.surface,
            color: PRES.textPrimary,
            opacity: 0,
            '&:hover': { bgcolor: PRES.surface },
            ...focusVisibleSx,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
        {canEdit && (
          <MenuItem onClick={() => { setMenuEl(null); setRenameValue(title); setRenameOpen(true); }}>
            {COPY.renamePresentation}
          </MenuItem>
        )}
        {canCreate && (
          <MenuItem onClick={() => { setMenuEl(null); void onDuplicate(); }}>
            {COPY.duplicatePresentation}
          </MenuItem>
        )}
        {canCreate && onCreateLanguageVersion && (
          <MenuItem
            disabled={!canAddLanguageVersion}
            onClick={() => { setMenuEl(null); onCreateLanguageVersion(); }}
          >
            {COPY.createLanguageVersion}
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem disabled sx={{ opacity: 1, fontFamily: TYPE.ui, ...TYPE.caption }}>{COPY.moveToCategory}</MenuItem>
        )}
        {canEdit && (
          <MenuItem onClick={() => { setMenuEl(null); void onMoveToCategory(null); }}>{COPY.noDeckCategory}</MenuItem>
        )}
        {canEdit && moveTargets.map((target) => (
          <MenuItem key={target.id} onClick={() => { setMenuEl(null); void onMoveToCategory(target.id); }}>
            {target.label}
          </MenuItem>
        ))}
        {canEdit && (
          <MenuItem onClick={() => { setMenuEl(null); setConfirmOpen(true); }}>{COPY.delete}</MenuItem>
        )}
      </Menu>
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.renamePresentation}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.presentationName}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            disabled={!renameValue.trim()}
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={async () => {
              await onRename(renameValue.trim());
              setRenameOpen(false);
            }}
          >
            {COPY.confirm}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{COPY.confirmDeleteTitle}</DialogTitle>
        <DialogContent>{COPY.confirmDeletePresentation}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            color="error"
            onClick={async () => {
              setConfirmOpen(false);
              await onDelete();
            }}
            sx={{ textTransform: 'none' }}
          >
            {COPY.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoverCard;
